import { useHistory, useLocation } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import isEqual from 'hs-lodash/isEqual';
import { TABLE_STATE_PARAMS } from './myTeamTableStateStoreTypes';
import { tableFilters } from '../constants/filters';
import { completedDateOptions, dueDateOptions, tableSortBy } from '../constants/filters';
const createUrlParams = (state, defaultState, currentSearch) => {
  const searchParams = new URLSearchParams(currentSearch);

  // First remove any existing filter params
  TABLE_STATE_PARAMS.forEach(param => searchParams.delete(param));

  // Then add current filter values
  TABLE_STATE_PARAMS.forEach(paramName => {
    const value = state[paramName];
    const defaultValue = defaultState[paramName];
    // only add params that are not the same as the default value
    if (value !== undefined && !isEqual(value, defaultValue)) {
      const valueString = Array.isArray(value) ? value.join(',') : value.toString();
      searchParams.set(paramName, valueString);
    }
  });
  const search = searchParams.toString();
  return search ? `?${search}` : '';
};
export const parseCsvParam = param => {
  if (!param) return [];
  return param.split(',').filter(Boolean);
};
const hasStateChanged = (prevState, newState) => {
  return TABLE_STATE_PARAMS.some(key => {
    const prev = prevState[key] || [];
    const next = newState[key] || [];
    return !isEqual(prev, next);
  });
};

// Hook to sync store state with URL two-way (from URL on mount and from store on change)
export const useSyncTableStateWithUrl = tableStore => {
  const history = useHistory();
  const location = useLocation();
  const isInitialMount = useRef(true);
  const prevStateRef = useRef(tableStore.defaultState);

  // Initialise the store from URL on mount only
  useEffect(() => {
    if (isInitialMount.current) {
      tableStore.actions.initializeFromUrl(location.search);
      isInitialMount.current = false;
    }
  }, [tableStore.actions, location.search]);

  // Update URL when store changes, but only if the change didn't come from URL so we prevent an update loop
  useEffect(() => {
    if (!isInitialMount.current && hasStateChanged(prevStateRef.current, tableStore.state)) {
      const newSearch = createUrlParams(tableStore.state, tableStore.defaultState, location.search);
      history.replace(Object.assign({}, location, {
        search: newSearch
      }));
    }
    prevStateRef.current = tableStore.state;
  }, [history, location, tableStore.state, tableStore.defaultState]);
};
export const getUrlState = (params, defaultState, tableName) => {
  return {
    search: params.search || defaultState.search,
    teams: parseCsvParam(params.teams).map(Number),
    users: parseCsvParam(params.users).map(Number),
    categories: parseCsvParam(params.categories),
    tools: parseCsvParam(params.tools),
    certificationStatuses: parseCsvParam(params.certificationStatuses),
    playlists: parseCsvParam(params.playlists).map(Number),
    dueDate: params.dueDate && params.dueDate in dueDateOptions ? params.dueDate : defaultState.dueDate,
    completedDate: params.completedDate && params.completedDate in completedDateOptions ? params.completedDate : defaultState.completedDate,
    page: Number(params.page) || defaultState.page,
    pageSize: Number(params.pageSize) || defaultState.pageSize,
    sortBy: params.sortBy && tableSortBy[tableName].includes(params.sortBy) ? params.sortBy : defaultState.sortBy,
    sortOrder: params.sortOrder || defaultState.sortOrder,
    isUserPinned: params.isUserPinned === 'false' ? false : defaultState.isUserPinned,
    assignedByMe: params.assignedByMe === 'true' ? true : undefined
  };
};

// Remove any filters that are not relevant to this table
export const trimUrlState = (urlState, tableName) => {
  return Object.fromEntries(Object.entries(urlState).filter(([urlKey]) => ['page', 'pageSize', 'sortBy', 'sortOrder'].includes(urlKey) ||
  // @ts-expect-error checking for valid filter name
  tableFilters[tableName].includes(urlKey)));
};